import { useCallback, useEffect, useState } from "react";
import {
  deleteRequest,
  getRequest,
  patchRequest,
  postRequest,
} from "services/api";
import { LIMIT_DATA_PER_PAGE } from "utils/constants";
import { buildQueryString } from "utils/queryHelpers";
import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";

const defaultMessageInvoices = {
  data: [],
  pagination: null,
  error: null,
  loading: false,
};

const defaultFilterOptions = {
  search: "",
  currentPage: 1,
  totalPages: 1,
};

const useMessageInvoices = () => {
  const [messageInvoices, setMessageInvoices] = useState(
    defaultMessageInvoices
  );
  const [filterOptions, setFilterOptions] = useState(defaultFilterOptions);

  const queryString = buildQueryString({
    search: filterOptions.search || "",
    page: filterOptions.currentPage,
    limit: LIMIT_DATA_PER_PAGE,
  });

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      console.log("search", e.target.value);
      setFilterOptions((prevState) => ({
        ...prevState,
        search: e.target.value,
      }));
    }
  };

  const fetchMessageInvoices = useCallback(async () => {
    setMessageInvoices((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      const response = await getRequest(
        `/setting/message-invoice${queryString}`
      );
      if (response.success) {
        setMessageInvoices({
          data: response.data,
          pagination: response.pagination,
          error: null,
          loading: false,
        });

        setFilterOptions((prev) => ({
          ...prev,
        }));
        console.log("response", response);
      }
    } catch (error) {
      setMessageInvoices({
        data: [],
        pagination: null,
        error: error.message,
        loading: false,
      });
      console.error("error", error);
    }
  }, [queryString]);

  useEffect(() => {
    fetchMessageInvoices();
  }, [queryString]);

  const handlePageChange = (page) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const totalPages = messageInvoices.pagination?.last_page || 1;

  const Pagination = usePaginationWithComponent({
    totalPages: totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
  });

  return {
    messageInvoices,
    setMessageInvoices,
    handleSearch,
    filterOptions,
    Pagination,
    refetch: fetchMessageInvoices,
  };
};

export { useMessageInvoices };

export const useCreateMessageInvoice = ({
  messageInvoices,
  setMessageInvoices,
}) => {
  const createMessageInvoice = async (data) => {
    setMessageInvoices((prevState) => ({
      ...prevState,
      loading: true,
    }));
    try {
      const response = await postRequest("/setting/message-invoice", data);
      if (response.success) {
        setMessageInvoices((prevState) => ({
          ...prevState,
          loading: false,
        }));
        return {
          success: true,
          message: "Message invoice has been created",
        };
      }
    } catch (error) {
      setMessageInvoices((prevState) => ({
        ...prevState,
        loading: false,
      }));
      return {
        success: false,
        message: "Failed to create message invoice",
      };
    }
  };

  return { createMessageInvoice };
};

export const useUpdateMessageInvoice = ({
  messageInvoices,
  setMessageInvoices,
}) => {
  const updateMessageInvoice = async (id, data) => {
    setMessageInvoices((prevState) => ({
      ...prevState,
      loading: true,
    }));
    try {
      const response = await patchRequest(
        `/setting/message-invoice/${id}`,
        data
      );
      if (response.success) {
        setMessageInvoices((prevState) => ({
          ...prevState,
          loading: false,
        }));
        return {
          success: true,
          message: "Message invoice has been updated",
        };
      }
    } catch (error) {
      setMessageInvoices((prevState) => ({
        ...prevState,
        loading: false,
      }));
      return {
        success: false,
        message: "Failed to update message invoice",
      };
    }
  };

  return { updateMessageInvoice };
};

export const useRemoveMessageInvoice = ({
  messageInvoices,
  setMessageInvoices,
}) => {
  const removeMessageInvoice = async (id) => {
    setMessageInvoices((prevState) => ({
      ...prevState,
      loading: true,
    }));
    try {
      const response = await deleteRequest(`/setting/message-invoice/${id}`);
      if (response.success) {
        setMessageInvoices((prevState) => ({
          ...prevState,
          loading: false,
        }));
        return {
          success: true,
          message: "Message invoice has been deleted",
        };
      }
    } catch (error) {
      setMessageInvoices((prevState) => ({
        ...prevState,
        loading: false,
      }));
      return {
        success: false,
        message: "Failed to delete message invoice",
      };
    }
  };

  return { removeMessageInvoice };
};
